import styled from 'styled-components';
import React, { useState, useEffect } from 'react';

const Slider = ({ id, question, saveUpdatedAnswers }) => {
  // Determine slider boundaries based on the scale number
  const scaleNum = question.properties.scale;
  const minValue = scaleNum === 10 ? 0 : 1;
  const maxValue = scaleNum === 10 ? 10 : scaleNum;
  const range = maxValue - minValue;
  // Set default slider value at the midpoint
  const defaultValue = minValue + range / 2;

  const [sliderValue, setSliderValue] = useState(defaultValue);
  const [isDragging, setIsDragging] = useState(false);
  const [clickedVal, setClickedVal] = useState(false);

  // If the question changes, reset the slider value
  useEffect(() => {
    setSliderValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const track = document.getElementById('slider-track');
      if (!track) return;
      const rect = track.getBoundingClientRect();
      let newValue = ((e.clientX - rect.left) / rect.width) * range + minValue;
      newValue = Math.min(Math.max(newValue, minValue), maxValue);
      setSliderValue(newValue);
      saveValue(newValue);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      const rounded = Math.round(sliderValue);
      setSliderValue(rounded);
      saveValue(rounded);
    };

    const handleTouchMove = (e) => {
      if (!isDragging) return;
      const track = document.getElementById('slider-track');
      if (!track) return;
      const rect = track.getBoundingClientRect();
      let newValue = ((e.touches[0].clientX - rect.left) / rect.width) * range + minValue;
      newValue = Math.min(Math.max(newValue, minValue), maxValue);
      setSliderValue(newValue);
      saveValue(newValue);
    };

    const handleTouchEnd = () => {
      const rounded = Math.round(sliderValue);
      setIsDragging(false);
      setSliderValue(rounded);
      saveValue(rounded);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, sliderValue, minValue, maxValue, range]);

  const handleClick = (e) => {
    const track = document.getElementById('slider-track');
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const newValue = Math.round((x / rect.width) * range + minValue);
    setSliderValue(newValue);
    saveValue(newValue);
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleTouchStart = () => {
    setIsDragging(true);
  };

  const saveValue = (newValue) => {
    setClickedVal(true);
    const QuesResp = {
      q: question,
      response: newValue,
      type: 'quantitative',
    };
    saveUpdatedAnswers(QuesResp);
  };

  // Interpolate thumb color based on slider value
  const lerpColor = (color1, color2, t) => {
    const r = color1.r + t * (color2.r - color1.r);
    const g = color1.g + t * (color2.g - color1.g);
    const b = color1.b + t * (color2.b - color1.b);
    return { r, g, b };
  };

  const getThumbColor = () => {
    const color1 = { r: 248, g: 168, b: 176 };
    const color2 = { r: 254, g: 205, b: 141 };
    const color3 = { r: 149, g: 230, b: 210 };
    // Normalize sliderValue to a 0-1 range
    const t = (sliderValue - minValue) / range;
    if (t < 0.5232) {
      const newT = t / 0.5232;
      const interpolatedColor = lerpColor(color1, color2, newT);
      return `rgb(${interpolatedColor.r}, ${interpolatedColor.g}, ${interpolatedColor.b})`;
    } else {
      const newT = (t - 0.5232) / (1 - 0.5232);
      const interpolatedColor = lerpColor(color2, color3, newT);
      return `rgb(${interpolatedColor.r}, ${interpolatedColor.g}, ${interpolatedColor.b})`;
    }
  };

  const thumbPosition = {
    left: `calc(${((sliderValue - minValue) / range) * 100}% - 10px)`,
  };

  // Calculate the number of dots on the track based on the dynamic range
  const dotCount = maxValue - minValue + 1;
  const dots = Array.from({ length: dotCount }, (_, index) => (
    <Dot key={index} style={{ left: `${(index / (dotCount - 1)) * 100}%` }} />
  ));

  return (
    <Container>
      <TrackWrapper  
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <Track id="slider-track">
          <DotsContainer>{dots}</DotsContainer>
          <Thumb
            style={{
              ...thumbPosition,
              backgroundColor: getThumbColor(),
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            id={`thumb_${id}`}
          >
            <Tooltip clicked={clickedVal}>
              {Math.round(sliderValue)}
              <Triangle />
            </Tooltip>
          </Thumb>
        </Track>
      </TrackWrapper>
      <ScaleLabelsContainer>
        <ScaleLabel>{question.properties.lowLabel}</ScaleLabel>
        <ScaleLabel>Neutral</ScaleLabel>
        <ScaleLabel>{question.properties.highLabel}</ScaleLabel>
      </ScaleLabelsContainer>
    </Container>
  );
};

export default Slider;

/* ---------------- Styled Components ---------------- */

const Container = styled.div`
  width: 100%;
  padding-top: 70px;
  user-select: none;
`;

const ScaleLabel = styled.span`
  font-size: 14px;
  @media (max-width: 500px) {
    font-size: ${props => props.language === 'EN' ? '14px' : '12px'};
  }
`;

const ScaleLabelsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
`;

const TrackWrapper = styled.div`
  position: relative;
  height: 20px; /* 10px above and below the track */
  cursor: pointer;
  user-select: none;
`;

const Track = styled.div`
  width: 100%;
  height: 6px;
  background: linear-gradient(
    to right,
    #f8a8b0 0%,
    #fecd8d 52.32%,
    #95e6d2 100%
  );
  position: relative;
  border-radius: 5px;
  cursor: pointer;
`;

const DotsContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  display: flex;
  justify-content: space-between;
`;

const Dot = styled.div`
  width: 4px;
  height: 4px;
  background-color: #fff;
  border-radius: 50%;
  position: absolute;
  transform: translateY(-50%);
`;

const Thumb = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  user-select: none;
  border: 4px solid white;
  box-shadow: 0px 8px 20px rgba(63, 134, 254, 0.08);
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #666d79;
  border-radius: 8px;
  width: 33px;
  height: 29px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => (props.clicked ? '#FFFFFF' : '#D6DBDF')};
  font-size: 14px;
  font-weight: ${props => (props.clicked ? '700' : '400')};
  margin-bottom: 8px;
`;

const Triangle = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 4px 4px 0;
  border-color: #666d79 transparent transparent;
`;
